//src/components/Playdate/PlaydateInviteResponseUnit.tsx
import React, { useState, useEffect } from "react";
import styles from "./PlaydateInviteResponseUnit.module.css";
import { truncateContent } from "../../utils/ContentTruncationComputer";
import {
    PlaydateInviteResponse,
    DayPart,
} from "../../gql/types";
import ParticipantSummaryRow from "./ParticipantSummaryRow";
import InfoTag, { TagVariant } from "./InfoTag";
import downArrowIcon from '../../assets/icons/downArrow.svg';
import upArrowIcon from '../../assets/icons/upArrow.svg';
import TimeSelectionDisplayView from "../time/TimeSelectionDisplayView";


interface PlaydateInviteResponseUnitProps {
    inviteResponse: PlaydateInviteResponse;
}

const PlaydateInviteResponseUnit: React.FC<PlaydateInviteResponseUnitProps> = ({
    inviteResponse,
}) => {

    const [expanded, setExpanded] = useState(false);
    const [showFullMsg, setShowFullMsg] = useState(false);

    //1. Handle early return if inviteResponse is invalid
    const isInvalid = (
        !inviteResponse ||
        !inviteResponse.guest ||
        !inviteResponse.responseType ||
        !inviteResponse.inviteID
    );

    if (isInvalid) {
        return <div></div>;
    }

    //1. Ingest PlaydateInviteResponse data, truncate message if needed
    const msg = inviteResponse.message || "";
    const truncatedMsg = truncateContent(msg);
    const longMsg = msg !== truncatedMsg;

    //2. Handle middle section Infotag
    let label: string;
    let variant: TagVariant;

    switch (inviteResponse.responseType) {
        case "MAYBE":
            label = "Pending";
            variant = TagVariant.DEFAULT;
            break;
        case "ACCEPT":
            label = "Confirmed";
            variant = TagVariant.CONFIRMED;
            break;
        case "REJECT":
            label = "Can't Make It";
            variant = TagVariant.REJECTED;
            break;
        case "NO_RESPONSE":
            label = "Pending";
            variant = TagVariant.DEFAULT;
            break;
        default:
            label = inviteResponse.responseType || "Default";
            variant = TagVariant.DEFAULT;
    }

    //3. Handle Reject and No Response Scenario and check if no msg, make carot invisible. Also, if no message and no daypart, setNoCarot to true
    const noCarot = (() => {
        const isReject = inviteResponse.responseType === "REJECT";
        const isNoResponse = inviteResponse.responseType === "NO_RESPONSE";
        const noMessage = !inviteResponse.message || inviteResponse.message.length === 0;
        const noDayPart = !inviteResponse.selectedScheduleOptions || inviteResponse.selectedScheduleOptions.length === 0;

        return (isReject && noMessage) || (isNoResponse && noMessage) || (noDayPart && noMessage);
    })();


    //4. Handle click event and show date options and details
    const handleExpandClick = () => {
        const newExpanded = !expanded;
        setExpanded(newExpanded);
    };

    //5. Handle date options and details
    const schedule_options = Array.isArray(inviteResponse.selectedScheduleOptions)
        ? inviteResponse.selectedScheduleOptions
        : [];

    // Process DayPart selections
    const processedDayParts = schedule_options
        .filter(opt => opt.dayPart !== null && opt.dayPart !== undefined)
        .map(opt => ({
            selectedDate: opt.date,
            selectedDayPart: opt.dayPart as DayPart
        }));

    // Process Time selections
    const processedTimes = schedule_options
        .filter(opt => opt.startTime && opt.endTime)
        .map((opt, index) => ({
            id: index,
            date: opt.date,
            startTime: opt.startTime,
            endTime: opt.endTime
        }));

    return (
        <div
            className={styles.playdateInviteResponseUnit}
            data-testid="playdate-invite-unit"
        >
            <div className={`${styles.mainUnit}`}
                onClick={!noCarot ? handleExpandClick : undefined}
            >
                <div className={styles.leftSection}>
                    <ParticipantSummaryRow participant={inviteResponse.guest} truncationLength={15} textSize="small" />
                </div>

                <div className={`${styles.rightSection} ${noCarot ? styles.noCarotPadding : styles.carotPadding}`}>
                    <InfoTag label={label} variant={variant} data-testid="info-tag" />
                    {!noCarot && <button className={styles.expandButton} onClick={handleExpandClick} data-testid="expand-button">
                        {!expanded ? (
                            <img src={downArrowIcon} alt="Expand" />
                        ) : (
                            <img src={upArrowIcon} alt="Shrink" />
                        )}
                    </button>}
                </div>
            </div>

            {expanded && (
                <>
                    <div className={styles.medianBorderWrapper}>
                        <div className={styles.medianBorder}></div>
                    </div>

                    <div className={styles.expandedUnit}>
                        {processedDayParts.length > 0 && (
                            <TimeSelectionDisplayView
                                mode="daypart"
                                selectedDayParts={processedDayParts}
                                groupByDate={true}
                                noGap={true}
                            />
                        )}

                        {processedTimes.length > 0 && (
                            <TimeSelectionDisplayView
                                mode="time"
                                selectedTimes={processedTimes}
                            />
                        )}

                        {processedDayParts.length === 0 && (
                            <div className={styles.spacer}></div>
                        )}

                        {(msg.length > 0) && (
                            <div className={styles.truncatedMessageBox} data-testid="message-box">
                                <p className={styles.truncatedMessage}>
                                    {showFullMsg ? msg : truncatedMsg}
                                </p>

                                {longMsg && (
                                    <button
                                        className={styles.readMoreButton}
                                        onClick={() => setShowFullMsg(!showFullMsg)}
                                    >
                                        {showFullMsg ? "Read Less" : "Read More"}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default PlaydateInviteResponseUnit;

